import { CreateSubscriptionDto } from '@thirsty-camel/hump-club/src/modules/subscription/dto/CreateSubscriptionDto'
import { DeleteSubscriptionDto } from '@thirsty-camel/hump-club/src/modules/subscription/dto/DeleteSubscriptionDto'
import type SubscriptionEntity from '@thirsty-camel/hump-club/src/modules/subscription/subscription.entity'
import { useState } from 'react'
import { QueryConfig } from 'react-query'

import { useBackendQuery } from '../useBackendQuery'
import { useHumpClubMutation } from '../useHumpClubMutation'
import { useToast } from '../useToast'

export interface UseProductSubscriptionQueryProps {
  /** The id of the product. */
  product?: string
  /** The id of the logged in user. */
  user?: string
  token?: string
}

export function useProductSubscriptionQuery<SubscriptionEntity>(
  options: UseProductSubscriptionQueryProps = {},
  config?: QueryConfig<any>,
) {
  const { product, user, token } = options
  const params = { product, user, token }

  const [isSubscribing, setIsSubscribing] = useState<boolean>(false)
  const [isUnsubscribing, setIsUnsubscribing] = useState<boolean>(false)

  const toast = useToast()
  const query = useBackendQuery<SubscriptionEntity>([`subscriptions`, { params, ...config }], {
    humpclub: true,
  })

  const [productSubscribe] = useHumpClubMutation<void, CreateSubscriptionDto>(
    ['subscriptions', { method: 'POST', ...config }],
    {
      onSuccess: () => {
        setIsSubscribing(false)
        toast({ status: 'success', title: 'Subscribed to notifications for this product' })
        query.refetch()
      },
      onError: e => {
        setIsSubscribing(false)
        alert(JSON.stringify(e))
        toast({ status: 'error', title: JSON.stringify(e) })
      },
    },
  )

  const [productUnsubscribe] = useHumpClubMutation<void, DeleteSubscriptionDto>(
    ['subscriptions', { method: 'DELETE', ...config }],
    {
      onSuccess: () => {
        setIsUnsubscribing(false)
        toast({ status: 'success', title: 'Unsubscribed from notifications for this product!' })
        query.refetch()
      },
      onError: e => {
        setIsUnsubscribing(false)
        toast({ status: 'error', title: e?.message })
      },
    },
  )

  const handleSubscribe = params => {
    setIsSubscribing(true)
    productSubscribe(params)
  }

  const handleUnsubscribe = params => {
    setIsUnsubscribing(true)
    productUnsubscribe(params)
  }

  return {
    ...query,
    isSubscribed: !!query.data,
    isSubscribing,
    isUnsubscribing,
    productSubscribe: () => handleSubscribe(params),
    productUnsubscribe: () => handleUnsubscribe(params),
  }
}

export default useProductSubscriptionQuery
